import React, { useEffect, useMemo, useState } from 'react';
import { fetchInstancias } from './api';

const normalizeInstanceValue = (value) => {
  if (value === undefined || value === null) {
    return '';
  }
  const text = String(value).trim();
  return text;
};

const normalizeInstances = (payload) => {
  const source = (() => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (payload && typeof payload === 'object') return Object.values(payload);
    return [];
  })();

  const seenIds = new Set();
  const instances = [];

  source.forEach((item, index) => {
    if (!item && item !== 0) {
      return;
    }

    if (typeof item === 'string') {
      const name = item.trim();
      if (!name) return;
      const syntheticId = `string-${index}`;
      if (!seenIds.has(syntheticId)) {
        seenIds.add(syntheticId);
        instances.push({ id: syntheticId, name });
      }
      return;
    }

    if (typeof item !== 'object') {
      return;
    }

    const idCandidate = normalizeInstanceValue(
      item.id ??
      item.ID ??
      item.instancia_id ??
      item.id_instancia ??
      item.fk_instancia ??
      item.pk_instancia ??
      item.codigo ??
      item.code
    );
    const fallbackId = idCandidate || `instancia-${index}`;
    if (seenIds.has(fallbackId)) {
      return;
    }

    const nameCandidate = normalizeInstanceValue(
      item.nombre ??
      item.name ??
      item.descripcion ??
      item.titulo ??
      item.label ??
      ''
    );

    instances.push({
      id: fallbackId,
      name: nameCandidate || `Instancia ${fallbackId}`,
      description:
        normalizeInstanceValue(item.detalle ?? item.descripcion_larga ?? item.descripcion ?? '') || null,
      raw: item,
    });
    seenIds.add(fallbackId);
  });

  return instances;
};

const InstanceSelector = ({ onSelect, onCancel, initialSelection }) => {
  const [state, setState] = useState({ loading: true, error: null, instances: [] });
  const [search, setSearch] = useState('');
  const [manualId, setManualId] = useState(initialSelection?.id || '');
  const [manualName, setManualName] = useState(initialSelection?.name || '');
  const [reloadIndex, setReloadIndex] = useState(0);

  useEffect(() => {
    setManualId(initialSelection?.id || '');
    setManualName(initialSelection?.name || '');
  }, [initialSelection]);

  useEffect(() => {
    let isMounted = true;

    const loadInstances = async () => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const payload = await fetchInstancias();
        if (!isMounted) return;
        setState({ loading: false, error: null, instances: normalizeInstances(payload) });
      } catch (error) {
        if (!isMounted) return;
        setState({
          loading: false,
          error: error instanceof Error ? error.message : 'No se pudieron cargar las instancias.',
          instances: [],
        });
      }
    };

    loadInstances();

    return () => {
      isMounted = false;
    };
  }, [reloadIndex]);

  const filteredInstances = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return state.instances;
    }
    return state.instances.filter((instance) => {
      const name = instance?.name?.toLowerCase?.() || '';
      const id = instance?.id?.toLowerCase?.() || '';
      return name.includes(term) || id.includes(term);
    });
  }, [search, state.instances]);

  const handleManualSubmit = (event) => {
    event.preventDefault();
    const sanitizedId = manualId.trim();
    if (!sanitizedId) {
      return;
    }
    const payload = {
      id: sanitizedId,
      name: manualName.trim() || `Instancia ${sanitizedId}`,
      description: null,
    };
    if (typeof onSelect === 'function') {
      onSelect(payload);
    }
  };

  const handleSelect = (instance) => {
    if (typeof onSelect === 'function') {
      onSelect(instance);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Selecciona una instancia</h1>
            <p className="text-gray-600 mt-1">Elige con qué instancia quieres trabajar en esta sesión.</p>
          </div>
          {typeof onCancel === 'function' && (
            <button
              type="button"
              onClick={onCancel}
              className="self-start px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              Volver
            </button>
          )}
        </div>

        <div className="mb-6">
          <label className="sr-only" htmlFor="instance-search">
            Buscar instancia
          </label>
          <input
            id="instance-search"
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nombre o ID"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>

        <div className="min-h-[180px]">
          {state.loading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-500">Cargando instancias...</p>
            </div>
          ) : state.error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 flex flex-col gap-4">
              <p>{state.error}</p>
              <div>
                <button
                  type="button"
                  onClick={() => setReloadIndex((prev) => prev + 1)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Reintentar
                </button>
              </div>
            </div>
          ) : filteredInstances.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4">
              No se encontraron instancias con ese criterio.
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredInstances.map((instance) => (
                <div
                  key={instance.id}
                  className="border border-gray-200 rounded-xl p-4 flex flex-col justify-between"
                >
                  <div className="mb-4">
                    <h2 className="text-lg font-semibold text-gray-800">{instance.name}</h2>
                    <p className="text-sm text-gray-500">ID: {instance.id}</p>
                    {instance.description && (
                      <p className="text-sm text-gray-600 mt-2">{instance.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSelect(instance)}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  >
                    Trabajar con esta instancia
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 border-t border-gray-100 pt-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-2">¿No ves tu instancia?</h2>
          <p className="text-sm text-gray-600 mb-4">
            Introduce el identificador manualmente para continuar.
          </p>
          <form className="grid gap-4 md:grid-cols-[2fr,2fr,1fr]" onSubmit={handleManualSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="manual-instance-id">
                ID de instancia
              </label>
              <input
                id="manual-instance-id"
                type="text"
                value={manualId}
                onChange={(event) => setManualId(event.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="manual-instance-name">
                Nombre (opcional)
              </label>
              <input
                id="manual-instance-name"
                type="text"
                value={manualName}
                onChange={(event) => setManualName(event.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Nombre para identificarla"
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                className="w-full px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                Usar instancia
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InstanceSelector;